import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Search,
  RefreshCw,
  Sparkles,
  Filter,
  CheckCircle2,
  Clock,
  XCircle,
} from "lucide-react";

const fmt = (n) => `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const STATUS_META = {
  pending:   { label: "pending",   tone: "amber",   Icon: Clock },
  completed: { label: "completed", tone: "emerald", Icon: CheckCircle2 },
  approved:  { label: "approved",  tone: "emerald", Icon: CheckCircle2 },
  rejected:  { label: "rejected",  tone: "rose",    Icon: XCircle },
};

const TONES = {
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  amber:   "bg-amber-500/15 text-amber-300 border-amber-500/30",
  rose:    "bg-rose-500/15 text-rose-300 border-rose-500/30",
  white:   "bg-white/5 text-white/60 border-white/10",
};

export default function History() {
  const [tab, setTab] = useState("deposits");
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setRefreshing(true);
    try {
      const [{ data: d }, { data: w }] = await Promise.all([
        api.get("/agent/deposits?limit=200"),
        api.get("/agent/withdrawals?limit=200"),
      ]);
      setDeposits(d.items || []);
      setWithdrawals(w.items || []);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totals = useMemo(() => ({
    deposits: deposits.reduce((s, x) => s + (x.amount || 0), 0),
    approved: withdrawals.filter((x) => x.status === "approved").reduce((s, x) => s + (x.amount || 0), 0),
    rejected: withdrawals.filter((x) => x.status === "rejected").length,
    pending: withdrawals.filter((x) => x.status === "pending").length,
  }), [deposits, withdrawals]);

  const filterFn = (it) => {
    // Normalize the query — strip common prefixes (@, #, ৳, BDT) and whitespace
    const raw = (search || "").trim();
    const q = raw.replace(/^[@#]/, "").replace(/[৳$₹]/g, "").replace(/[, ]/g, "").toLowerCase();
    const matchSearch = !q || (
      (it.player_name || "").toLowerCase().includes(q) ||
      (it.username || "").toLowerCase().includes(q) ||
      String(it.player_id || "").toLowerCase().includes(q) ||
      (it.note || "").toLowerCase().includes(q) ||
      (it.code || "").toLowerCase().includes(q) ||
      String(it.amount || "").includes(q) ||
      String(it.id || "").toLowerCase().includes(q)
    );
    const matchStatus = statusFilter === "all" || it.status === statusFilter;
    return matchSearch && matchStatus;
  };

  const visibleDeposits = useMemo(() => deposits.filter(filterFn), [deposits, search, statusFilter]);
  const visibleWithdrawals = useMemo(() => withdrawals.filter(filterFn), [withdrawals, search, statusFilter]);

  const statusOptions = tab === "deposits"
    ? ["all", "completed", "pending", "rejected"]
    : ["all", "approved", "pending", "rejected"];

  return (
    <div data-testid="agent-history-page" className="max-w-[1300px] mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[2px] text-violet-300 border border-violet-500/30 bg-violet-500/10 rounded-full px-3 py-1 mb-3">
            <Sparkles size={11} /> Activity Log
          </span>
          <h1 className="text-[28px] font-black tracking-tight leading-none">
            Your <span className="bg-gradient-to-r from-emerald-300 to-violet-300 bg-clip-text text-transparent">history</span>
          </h1>
          <p className="text-sm text-white/55 mt-2">All deposits you've sent and withdrawals routed through you.</p>
        </div>
        <button
          onClick={load}
          className="h-10 px-4 inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 rounded-xl text-[12.5px] font-semibold border border-white/10"
          data-testid="history-refresh"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Total deposits sent" value={fmt(totals.deposits)} sub={`${deposits.length} txns`} tone="emerald" mono Icon={ArrowDownToLine} />
        <SummaryCard label="Approved withdrawals" value={fmt(totals.approved)} sub={`${withdrawals.filter(x=>x.status==='approved').length} approved`} tone="violet" mono Icon={CheckCircle2} />
        <SummaryCard label="Pending withdrawals" value={totals.pending} sub="awaiting code" tone="amber" Icon={Clock} />
        <SummaryCard label="Rejected" value={totals.rejected} sub="refunded" tone="rose" Icon={XCircle} />
      </div>

      {/* Tabs + filters bar */}
      <div className="rounded-2xl border border-white/[0.08] overflow-hidden"
        style={{ background: "linear-gradient(180deg, rgba(13,22,38,0.92) 0%, rgba(10,17,30,0.92) 100%)" }}
      >
        <div className="px-5 pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/[0.06]">
          {/* Tabs */}
          <div className="inline-flex p-1 bg-white/[0.03] border border-white/[0.06] rounded-xl">
            <TabButton active={tab === "deposits"} onClick={() => { setTab("deposits"); setStatusFilter("all"); }} icon={ArrowDownToLine} testid="history-tab-deposits">
              Deposits <span className="ml-1.5 text-[10px] opacity-70">({deposits.length})</span>
            </TabButton>
            <TabButton active={tab === "withdrawals"} onClick={() => { setTab("withdrawals"); setStatusFilter("all"); }} icon={ArrowUpFromLine} testid="history-tab-withdrawals">
              Withdrawals <span className="ml-1.5 text-[10px] opacity-70">({withdrawals.length})</span>
            </TabButton>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2.5 pb-3 sm:pb-4 flex-wrap">
            <div className="relative flex items-center gap-2">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                  placeholder="Search player, ID, note, amount…"
                  className="h-9 pl-8 pr-8 w-64 bg-white/[0.03] border border-white/[0.08] rounded-lg text-[12px] focus:border-emerald-500/60 focus:outline-none focus:ring-2 focus:ring-emerald-500/15 transition"
                  data-testid="history-search"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 grid place-items-center text-white/40 hover:text-white hover:bg-white/10 rounded transition"
                    aria-label="Clear search"
                    data-testid="history-search-clear"
                  >
                    <XCircle size={13} />
                  </button>
                )}
              </div>
              {search && (
                <span
                  data-testid="history-search-count"
                  className="text-[10.5px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-2 py-0.5"
                >
                  {(tab === "deposits" ? visibleDeposits.length : visibleWithdrawals.length)} match
                  {(tab === "deposits" ? visibleDeposits.length : visibleWithdrawals.length) === 1 ? "" : "es"}
                </span>
              )}
            </div>
            <div className="relative">
              <Filter size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 pl-7 pr-3 bg-white/[0.03] border border-white/[0.08] rounded-lg text-[12px] focus:border-emerald-500/60 focus:outline-none capitalize"
                data-testid="history-status-filter"
              >
                {statusOptions.map((s) => (
                  <option key={s} value={s} className="bg-[#0d1626]">{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <div className="h-8 w-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tab === "deposits" ? (
          visibleDeposits.length === 0 ? (
            <Empty label={deposits.length ? "No deposits match your filter" : "No deposits yet"} hint={deposits.length ? "Try clearing the search or status filter." : "Head to Deposit to user to start sending funds."} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="history-deposits-table">
                <thead>
                  <tr className="text-left text-white/40 bg-white/[0.015]">
                    <Th>User</Th>
                    <Th>Note</Th>
                    <Th align="right">Amount</Th>
                    <Th>Status</Th>
                    <Th>Date</Th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDeposits.map((d) => (
                    <tr key={d.id} className="border-t border-white/[0.04] hover:bg-white/[0.015] transition group">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg grid place-items-center font-black text-[11px] flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, rgba(52,211,153,0.18), rgba(16,185,129,0.06))", color: "#34d399", border: "1px solid rgba(52,211,153,0.3)" }}>
                            {(d.player_name || "U").charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-white truncate">{d.player_name}</div>
                            <div className="text-[10.5px] text-white/40 font-mono">@{d.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-white/55 truncate max-w-xs">{d.note || <span className="text-white/25">—</span>}</td>
                      <td className="px-5 py-3.5 text-right font-mono font-extrabold text-emerald-300 tabular-nums">{fmt(d.amount)}</td>
                      <td className="px-5 py-3.5"><StatusBadge s={d.status} /></td>
                      <td className="px-5 py-3.5 text-white/45 whitespace-nowrap">{new Date(d.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          visibleWithdrawals.length === 0 ? (
            <Empty label={withdrawals.length ? "No withdrawals match your filter" : "No withdrawals yet"} hint={withdrawals.length ? "Try clearing the search or status filter." : "Pending withdrawals will appear here automatically."} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]" data-testid="history-withdrawals-table">
                <thead>
                  <tr className="text-left text-white/40 bg-white/[0.015]">
                    <Th>User</Th>
                    <Th>Code</Th>
                    <Th align="right">Amount</Th>
                    <Th>Status</Th>
                    <Th>Date</Th>
                  </tr>
                </thead>
                <tbody>
                  {visibleWithdrawals.map((w) => (
                    <tr key={w.id} className="border-t border-white/[0.04] hover:bg-white/[0.015] transition">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg grid place-items-center font-black text-[11px] flex-shrink-0"
                            style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#1a1208" }}>
                            {(w.player_name || "U").charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-white truncate">{w.player_name}</div>
                            <div className="text-[10.5px] text-white/40 font-mono">@{w.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className="font-mono text-amber-300 text-[12.5px] font-extrabold tabular-nums tracking-wider bg-amber-500/10 border border-amber-500/25 rounded-md px-2 py-0.5">
                          {w.code}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right font-mono font-extrabold text-violet-300 tabular-nums">{fmt(w.amount)}</td>
                      <td className="px-5 py-3.5"><StatusBadge s={w.status} /></td>
                      <td className="px-5 py-3.5 text-white/45 whitespace-nowrap">{new Date(w.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, tone, mono, Icon }) {
  const tones = {
    emerald: { ring: "rgba(52,211,153,0.30)", text: "text-emerald-300", bg: "rgba(52,211,153,0.06)" },
    violet: { ring: "rgba(167,139,250,0.30)", text: "text-violet-300", bg: "rgba(167,139,250,0.06)" },
    amber: { ring: "rgba(251,191,36,0.30)", text: "text-amber-300", bg: "rgba(251,191,36,0.06)" },
    rose: { ring: "rgba(244,63,94,0.30)", text: "text-rose-300", bg: "rgba(244,63,94,0.06)" },
  };
  const t = tones[tone];
  return (
    <div className="relative overflow-hidden rounded-xl border p-4"
      style={{ background: t.bg, borderColor: t.ring }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="h-7 w-7 rounded-lg grid place-items-center" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${t.ring}` }}>
          <Icon size={13} className={t.text} />
        </div>
        <div className={`text-[10px] uppercase tracking-[1.5px] font-bold ${t.text}`}>{label}</div>
      </div>
      <div className={`text-[22px] font-black tracking-tight ${mono ? "font-mono tabular-nums" : ""}`}>{value}</div>
      {sub && <div className="text-[10.5px] text-white/45 mt-0.5">{sub}</div>}
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 h-9 rounded-lg text-[12.5px] font-bold inline-flex items-center gap-2 transition ${
        active
          ? "bg-emerald-500/15 text-emerald-300 shadow-[inset_0_0_0_1px_rgba(52,211,153,0.3)]"
          : "text-white/55 hover:text-white"
      }`}
    >
      <Icon size={13} />
      {children}
    </button>
  );
}

function Th({ children, align }) {
  return (
    <th className={`text-${align || "left"} px-5 py-3 text-[10px] uppercase tracking-[1.5px] font-bold`}>{children}</th>
  );
}

function StatusBadge({ s }) {
  const meta = STATUS_META[s] || { label: s, tone: "white", Icon: Clock };
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-bold border ${TONES[meta.tone]}`}>
      <Icon size={10} /> {meta.label}
    </span>
  );
}

function Empty({ label, hint }) {
  return (
    <div className="text-center py-14 px-4">
      <div className="h-14 w-14 rounded-2xl bg-white/[0.03] mx-auto grid place-items-center mb-3 border border-white/[0.05]">
        <Clock className="text-white/30" size={22} />
      </div>
      <div className="text-[13px] font-bold text-white/65">{label}</div>
      {hint && <div className="text-[11.5px] text-white/40 mt-1 max-w-sm mx-auto">{hint}</div>}
    </div>
  );
}
